import type { FC, ReactNode } from 'react'
import { Fragment } from 'react'
import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import Button from '@segment/matcha/components/Button'
import Tooltip from '@segment/matcha/components/Tooltip'
import Typography from '@segment/matcha/components/Typography'

import { InfoIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import TablePricingBase, * as TablePricing from '@segment/matcha/recipes/TablePricing'
import { CheckIcon } from '@segment/matcha/recipes/TablePricing/Icons'
import SignupButton from '@segment/matcha/recipes/SignupButton'

import { trackButtonClick } from '@segment/tracking/scripts'

import useMatchMedia from '@segment/utils/useMatchMedia'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import sections from './data'

const copy = {
  heading: `Compare CDP plans`,
}

const link = {
  text: `Get a demo`,
  url: `/demo/`,
}

const ComparePlans: FC = () => {
  const theme = useTheme()
  const isDesktopResolution = useMatchMedia(theme.breakpoints.up(`xxl`, false), true)
  const pageConfig = usePageConfig()

  return (
    <StyledSectionBase variant="centered" id="compare-plans">
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading>{copy.heading}</CopyText.Heading>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <TablePricingBase columns={3} scrollable={!isDesktopResolution}>
          <TablePricing.Header sticky={true} pinFirst={true} tokens={{ top: isDesktopResolution ? 128 : 0 }}>
            <TablePricing.HeaderCell />
            <TablePricing.HeaderCell title="Unify" headingVariant="pLargeBold">
              <StyledTypography variant="pLargeBold">Custom Pricing</StyledTypography>
              <SignupButton
                variant="outlined"
                size="medium"
                onClick={() =>
                  trackButtonClick({
                    buttonText: `Get a demo`,
                    targetUrl: `/signup/`,
                    buttonId: `pricing-cdp-page-unify-compare-table`,
                  })
                }
              >
                Get a demo
              </SignupButton>
            </TablePricing.HeaderCell>
            <TablePricing.HeaderCell title="Unify Plus" headingVariant="pLargeBold">
              <StyledTypography variant="pLargeBold">Custom Pricing</StyledTypography>
              <SignupButton
                variant="outlined"
                size="medium"
                onClick={() =>
                  trackButtonClick({
                    buttonText: ` Get a demo`,
                    targetUrl: `/signup/`,
                    buttonId: `pricing-cdp-page-unify-plus-compare-table`,
                  })
                }
              >
                Get a demo
              </SignupButton>
            </TablePricing.HeaderCell>
            <TablePricing.HeaderCell title="Engage" headingVariant="pLargeBold">
              <StyledTypography variant="pLargeBold">Custom Pricing</StyledTypography>
              <Button
                href={getAbsoluteUrl(link.url, pageConfig)}
                variant="outlined"
                color="primary"
                size="medium"
                onClick={() =>
                  trackButtonClick({
                    buttonText: link.text,
                    targetUrl: link.url,
                    buttonId: `pricing-cdp-page-engage-compare-table`,
                  })
                }
              >
                {link.text}
              </Button>
            </TablePricing.HeaderCell>
          </TablePricing.Header>
          <TablePricing.Body>
            {sections().map((section) => (
              <Fragment key={section.header}>
                <TablePricing.SectionHeader>{section.header}</TablePricing.SectionHeader>
                {section.rows.map((row) => (
                  <Fragment key={row.header}>
                    <TablePricing.Row
                      header={
                        <TablePricing.RowHeader
                          icon={
                            row.tooltip && (
                              <Tooltip label={row.tooltip}>
                                <InfoIcon color={theme.palette.twilio.gray[40]} />
                              </Tooltip>
                            )
                          }
                        >
                          {row.header}
                        </TablePricing.RowHeader>
                      }
                    >
                      {row.cells.map((cell, index) => (
                        <FeatureCell key={index} feature={cell} />
                      ))}
                    </TablePricing.Row>
                    {row?.subRows?.map((subRow) => (
                      <TablePricing.Row
                        key={subRow.header}
                        header={
                          <TablePricing.RowSubHeader
                            icon={
                              subRow.tooltip && (
                                <Tooltip label={subRow.tooltip}>
                                  <InfoIcon color={theme.palette.twilio.gray[40]} />
                                </Tooltip>
                              )
                            }
                          >
                            {subRow.header}
                          </TablePricing.RowSubHeader>
                        }
                      >
                        {subRow.cells.map((subRowCell, index) => (
                          <FeatureCell key={index} feature={subRowCell} />
                        ))}
                      </TablePricing.Row>
                    ))}
                  </Fragment>
                ))}
                <TablePricing.EmptyRow />
              </Fragment>
            ))}
          </TablePricing.Body>
        </TablePricingBase>
      </Section.Contents>
    </StyledSectionBase>
  )
}

const StyledSectionBase = styled(SectionBase)`
  overflow: initial;
`

const StyledTypography = styled(Typography)`
  margin-top: 0;
`

interface FeatureCellProps {
  feature: ReactNode
}

const FeatureCell: FC<FeatureCellProps> = ({ feature }) => (
  <TablePricing.Cell>
    {typeof feature === `boolean` ? <FeatureIcon featureEnabled={feature} /> : feature}
  </TablePricing.Cell>
)

interface FeatureIconProps {
  featureEnabled: boolean
}

const FeatureIcon: FC<FeatureIconProps> = ({ featureEnabled }) => (featureEnabled ? <CheckIcon /> : <>—</>)

export default ComparePlans
